import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, mergeProps as _mergeProps, vShow as _vShow } from "vue"

const _hoisted_1 = { class: "contentHeader flex" }
const _hoisted_2 = { class: "contentBox" }
const _hoisted_3 = { class: "block w-full" }
const _hoisted_4 = { class: "flex mb-4" }
const _hoisted_5 = ["title"]
const _hoisted_6 = /*#__PURE__*/_createElementVNode("span", { class: "icon-neu" }, null, -1)
const _hoisted_7 = [
  _hoisted_6
]
const _hoisted_8 = ["disabled", "title"]
const _hoisted_9 = /*#__PURE__*/_createElementVNode("span", { class: "icon-downlaod" }, null, -1)
const _hoisted_10 = [
  _hoisted_9
]
const _hoisted_11 = ["disabled", "title"]
const _hoisted_12 = /*#__PURE__*/_createElementVNode("span", { class: "icon-loeschen" }, null, -1)
const _hoisted_13 = [
  _hoisted_12
]
const _hoisted_14 = ["disabled", "title"]
const _hoisted_15 = /*#__PURE__*/_createElementVNode("span", { class: "icon-status_bald_inaktiv" }, null, -1)
const _hoisted_16 = [
  _hoisted_15
]
const _hoisted_17 = { class: "ml-auto" }
const _hoisted_18 = { class: "text-center" }

import {onBeforeUnmount, onMounted, reactive, ref} from "vue";
import {BryntumGrid} from "@bryntum/grid-vue-3";
import {Menu, Store, StringHelper} from "@bryntum/grid";
import GridInputFilter from "@/components/GridInputFilter";
import {useGlobalStore} from "@/stores/GlobalStore";
import {UserInfo} from "@/models/user/UserInfo";
import {ErrorLocationType, PopupType, TableType} from '@/utilities/enums/Enums';
import ErrorHandler from "@/utilities/helpers/ErrorHandler";
import FilterService from "@/utilities/services/FilterService";
import exportFromJSON from "export-from-json";
import {UserCsvExport} from "@/models/CsvExportModels";
import {PermissionService} from "@/utilities/services/PermissionService";
import dialog from "@/dialog";
import {GeneralResponse} from "@/models/api/Responses";
import {$t} from "@/utilities/i18n/config";
import Error from "@/components/Error";
import {useI18n} from "vue-i18n";
import {checkboxColumnConfig, defaultSpaceAroundTableOnPage, permissionColumn} from "@/AppConfig";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import NullOrUndefinedHelper from "@/utilities/helpers/NullOrUndefinedHelper";
import {UserService} from "@/utilities/services/entities/UserService";


export default /*#__PURE__*/_defineComponent({
  __name: 'Users',
  setup(__props) {

const { t } = useI18n();
const userService = new UserService();
const permissionService = new PermissionService();

const gridStore = new Store();
const globalStore = useGlobalStore();

let selectedUsers = ref([]);

const deleteSelection = async() => {
  //drop call without selection
  if(!selectedUsers.value?.some(()=>true)) return;

  const answer = await dialog
      .okText(t("global.delete"))
      .cancelText(t("global.cancel"))
      .confirm(t("users.confirmDeletionMulti", { count:selectedUsers.value.length}));

  if (answer) {
    let someEntriesFailed = false;
    for (const user of selectedUsers.value){
      const resp = await userService.deleteUser(user.id);

      if(resp === null || resp === undefined || resp?.statusCode !== 0){
        ErrorHandler.handleError(resp, ErrorLocationType.table);
        someEntriesFailed = true;
        continue;
      }

      gridStore.applyChangeset({removed:[{id:user.id}]});
    }

    if (someEntriesFailed) return;

    globalStore.showSuccess(t("global.deleted"));
  }
};

const currentWindowHeight = ref("");
const recalcGridHeight = () => {
  currentWindowHeight.value = `${window.innerHeight-defaultSpaceAroundTableOnPage}px`;
};

const newUser = async () => {
  const user = new UserInfo();

  // call API-Service and set identificationNumber from next free Id
  const nextIdResp = await userService.getNextFreeUserId();
  if(nextIdResp === null || nextIdResp === undefined || nextIdResp?.statusCode !== 0){
    ErrorHandler.handleError(nextIdResp, ErrorLocationType.table);
    return;
  }

  user.identificationNumber = nextIdResp.identifikationNumber;

  globalStore.openPopup({
    data:user,
    action:addOrUpdateUser,
    resetButton:false,
    title:$t("popups.userEditor.title.new"),
    type:PopupType.userPopup
  });
}

const exportCSV = () => {
  let data = selectedUsers.value;

  // remove unnecessary properties from csv
  data = data.map((item)=> new UserCsvExport(item.data ?? item));

  const fileName = $t('users.csvDownloadFilename');
  const exportType =  exportFromJSON.types.csv;

  exportFromJSON({
    data:data,
    fileName:fileName,
    exportType:exportType,
  });
};

const addOrUpdateUser = async () => {
  const user = globalStore.modal.contentObject as UserInfo;
  // cleaning up empty string or null string
  user.kid = NullOrUndefinedHelper(user.kid) ? null : user.kid;

  const userResp = await userService.addOrUpdateUsers(user);
  if (userResp === null || userResp === undefined || userResp.statusCode !== 0){
    ErrorHandler.handleError(userResp as GeneralResponse, ErrorLocationType.popup);
    return;
  }

  if (user.id === "") {
    user.id = userResp.id
    user.isActive = true;
  }
  // fix selection when it was an update -> replace old value in selected-array
  else if (selectedUsers.value.some(el => el.id === user.id)) {
    selectedUsers.value = selectedUsers.value.filter(el => el.id !== user.id);
    selectedUsers.value.push(user);
  }

  user.fullName = `${user.firstName} ${user.lastName}`;

  // in case of an update bryntum automatically replaces the entry with the same ID
  gridStore.add(user);
  // reapply sorting
  gridStore.sort(gridStore.sorters);
  // reapply filtering
  gridStore.filter({
    id: "userFilter",
    filterBy: FilterService.buildFilter(TableType[TableType.user])
  });

  globalStore.closeModal();
  globalStore.showSuccess(t('global.saved'));
}

const grid = ref(null);
const useGridConfig = {
  headerMenuFeature: false,
  height: currentWindowHeight,
  enableTextSelection: true,
  cellTooltipFeature: {
    hoverDelay      : 300,
    textContent     : true,
    hideOnDelegateChange: true,
  },
  selectionMode : {
    row	  : true,
    checkbox: checkboxColumnConfig,
    showCheckAll : true,
    deselectFilteredOutRecords: true
  },
  relayStoreEvents: true, // triggers important event (globalStore-/data-related) to get bryntum stores !
  columns :{
    data: [
      { id:'identificationNumber', field : 'identificationNumber', text: t('users.grid.identificationNumber'), flex : 2 },
      { id:'kid', field : 'kid', text: t('users.grid.kid'), flex : 1 },
      {
        id:'fullName',
        field : 'fullName',
        text: t('users.grid.fullName'),
        flex : 1,
        type: 'template',
        template: ({ record }) => {
					return StringHelper.xss`${record.data.fullName ?? ""}<div class='ml-2 gridInfoIcon'><span class='icon-info-1'></span></div>`;
        },
      },
      { id:'firstName', field : 'firstName', text: t('users.grid.firstName'), flex : 1 },
      { id:'lastName', field : 'lastName', text: t('users.grid.lastName'), flex : 1 },
      {
        id:'roles',
        field : 'roles',
        text: t('users.grid.roles'),
        flex : 1,
        type: 'template',
        template: ({ record }) => {
          // fix property array from [object] to [string] (happens after edititng user)
          if(record.data.roles.some(r => r.name !== undefined)) record.data.roles = record.data.roles.map(r => r.name)
          return record.data.roles?.join(", ");
        },
      },
      { id:'info', field : 'info', text: t('users.grid.info'), width: 100 },
      {
        id:'isActive',
        field : 'isActive',
        text: t('users.grid.isActive'),
        width: 36,
        type: 'template',
        tooltipRenderer: ({record}) => {
          if(record.data.isActive) {
            return StringHelper.xss`${t('users.grid.activeUser')}`
          } else {
            return StringHelper.xss`${t('users.grid.inactiveUser')}`
          }
        },
        template: ({ record }) => {
          if(record.data.isActive) {
            return "<span class='icon-status_aktiv text-green biggerGridIcon'></span>";
          } else {
            return '<div class="gridRowWarningButton"><span class="icon-status_bald_inaktiv bg-red text-white"></span></div>';
          }
        },
      },
      {
        id:'email',
        field : 'email',
        text: t('users.grid.email'),
        flex : 1,
        editor   : false,
        type: 'template',
        template: ({ record }) => {
          if (NullOrUndefinedHelper(record.data.email)) return ""
					return StringHelper.xss`<a class='gridLink' href='mailto:"${record.data.email}"'>${record.data.email ?? ""}</a>`;
        },
      },
      {
        id:'phoneNumber',
        field : 'phoneNumber',
        text: t('users.grid.phoneNumber'),
        flex : 1,
        type: 'template',
        template: ({ record }) => {
          if (NullOrUndefinedHelper(record.data.phoneNumber)) return ""
					return StringHelper.xss`<a class='gridLink' href='tel:"${record.data.phoneNumber}"'>${record.data.phoneNumber ?? ""}</a>`;
        },
      },
      {
        id:'menu',
        text	: '',
        minWidth: 36,
        width: 36,
        type	: 'widget',
        align: "center",
        hideable: false,
        resizable: false,
        sortable: false,
        filterable: false,
        widgets : [{
          type	 : 'button',
          width: 36,
          icon	 : 'icon-kontext_horizontal',
          onAction : ({ source: btn }) => {
            const menu = menuFactory(btn.cellInfo.record.data);
            menu.alignTo({ target: btn })
            menu.show();
          }
        }]
      },
    ],
    listeners:{
      async add(event:any){
        if(event.records.length === 1 && event.isMove[event.records[0].id]){
          await globalStore.saveUserColumnOrder(TableType[TableType.user], event.records[0].id, event.index.toString());
        }
      },
    }
  },
  store: gridStore,
  listeners: {
    selectionChange(event) {
      if(event.selection.length > 0 && event.selection[0]?.data === undefined) return;
      selectedUsers.value = selectedUsers.value.concat(event.selected.map(ur => ur.data))
      selectedUsers.value = selectedUsers.value.filter(u => !event.deselected.map(ur => ur.data).some(ur => u?.id === ur?.id));
    },
    datachange(){
      globalStore.currentItems = gridStore.count;
      globalStore.allItems = gridStore.originalCount;
    },
		cellClick({column, record}) {
			if (column.data.field === 'isActive') {
        if(!record.data.isActive) {
          if(permissionService.userHasPermission("ProviderManagementPolicy.CompanyWorker.Read")) {
            resetActivity(record);
          }
        }
			}
		},
  },
}
const gridConfig = reactive(useGridConfig);

const menuFactory = (record:object) => {
  const menu = new Menu({
    anchor   : true,
    autoShow : true,
    items	: [
      {
        text: t('global.edit'),
        cls: 'color',
        icon: 'icon-editieren',
        hidden: !permissionService.userHasPermission("UserAdministrator.User.Edit"),
        weight: 100,
        onItem : async ({ menu }) => {
          const fullUser = await userService.getUserById(menu.record.id, ErrorLocationType.table);

          if(fullUser === null || fullUser === undefined)return;

          fullUser.isActive = menu.record.isActive;

          globalStore.openPopup({
            data:fullUser,
            action:addOrUpdateUser,
            resetButton:false,
            title:$t("popups.userEditor.title.edit"),
            type:PopupType.userPopup}
          );
        }
      },
      {
        text   : t('users.menu.permission'),
        cls	: 'color',
        icon: 'icon-editieren',
        hidden: true,//!permissionService.userHasPermission("UserAdministrator.User.Role.Edit"),
        weight : 120
      },
      {
        text   : t('users.menu.activity'),
        cls	: 'color',
        icon: 'icon-reset',
				hidden: checkUserActivityOption(record),
        weight : 130,
        onItem: async ({ menu }) => {
          await resetActivity(menu.record);
        },
      },
      {
        text   : t('global.delete'),
        hidden: !permissionService.userHasPermission("UserAdministrator.User.Delete"),
        cls	: 'color',
        icon: 'icon-loeschen',
        weight : 140,
        onItem: async ({ menu }) => {
          const answer = await dialog
              .okText(t("global.delete"))
              .cancelText(t("global.cancel"))
              .confirm(t("users.confirmDeletionSingle", { name:menu.record.fullName}));

          if (answer) {
            const resp = await userService.deleteUser(menu.record.id);

            if(resp === null || resp === undefined || resp?.statusCode !== 0){
              ErrorHandler.handleError(resp, ErrorLocationType.table);
              return;
            }

            gridStore.applyChangeset({removed:[{id:menu.record.id}]});

            globalStore.showSuccess(t('users.deleted'));
          }
        }
      }
    ],
  });

  menu.setRecord(record);

  return menu;
};

const checkUserActivityOption = (record) => {
	if(permissionService.userHasPermission("UserAdministrator.User.Edit") && !record.isActive) {
		return false;
	} else {
		// hide button in menu
		return true;
	}
}
const resetActivity = async (record) => {
	const answer = await dialog
			.okText(t("global.reset"))
			.cancelText(t("global.cancel"))
			.confirm(t("users.resetActivity", {name: record.name}));

	if (answer) {
		var idList = [record.id];

		const resp = await userService.resetUserActivity(idList);

		if (resp === null || resp === undefined || resp?.statusCode !== 0) {
			ErrorHandler.handleError(resp, ErrorLocationType.table);
			return;
		}

		// update table
		record.isActive = true;
		gridStore.add(record);

		globalStore.showSuccess(`${record.fullName} reaktiviert`);
	}

}

const resetMultipleActivities = async () => {

  //drop call without selection
  if(!selectedUsers.value?.some(()=>true)) return;

  const answer = await dialog
      .okText(t("global.reset"))
      .cancelText(t("global.cancel"))
      .confirm(t("users.resetMultipleActivity", {count: selectedUsers.value.length}));

	if (answer) {

    var idList = [];

    // collect the selected user IDs
    for (const user of selectedUsers.value){
      idList.push(user.id);
    }

		const resp = await userService.resetUserActivity(idList);

		if (resp === null || resp === undefined || resp?.statusCode !== 0) {
			ErrorHandler.handleError(resp, ErrorLocationType.table);
			return;
		}

    // mark newly active users
    for (const user of selectedUsers.value){
      user.isActive = true;
      gridStore.add(user);
    }

		globalStore.showSuccess(t('users.inactiveUsersReactivated'));
	}

}

const setGridData = async ()=>{
  // start spinner
  globalStore.gridDataIsLoading = true;

  const users = await userService.getUsers();
  if (users === null || users === undefined || users.statusCode !== 0) {
    ErrorHandler.handleError(users, ErrorLocationType.table);
    return;
  }

  gridStore.setStoreData(users.userInfo);

  // apply filters
  gridStore.filter({
    id: "userFilter",
    filterBy: FilterService.buildFilter(TableType[TableType.user])
  });

  // apply hidden cols
  for(const c of grid.value.instance.value.columns.storage.values){
    const needsHiding = globalStore.userPreferences.userHiddenColumns.some((thc) => thc.attributeName === c.data.field && thc.tableName === TableType[TableType.user]);

    if(needsHiding && !c.hidden) {
      c.hide();
    }
    else if(!needsHiding && c.hidden && !permissionColumn.includes(c.id)) {
      c.show();
    }
  }

  globalStore.reorderColumns(TableType[TableType.user]);

  globalStore.closeModal();
  // stop spinner
  globalStore.gridDataIsLoading = false;
};

onMounted(async () => {
  // calculate grid height and sub to window resize event
  recalcGridHeight();
  window.addEventListener('resize', recalcGridHeight);

  globalStore.setGridStore(ref(gridStore));
  globalStore.setGridColumnStore(ref(grid.value?.instance.value?.columns));

  await setGridData();
})

onBeforeUnmount(()=>{
  window.removeEventListener('resize', recalcGridHeight);

  globalStore.setGridStore(null);
  globalStore.setGridColumnStore(null);

  globalStore.currentItems = 0;
  globalStore.allItems = 0;
});

return (_ctx: any,_cache: any) => {
  const _directive_permission = _resolveDirective("permission")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("h4", null, _toDisplayString(_unref($t)('users.title')), 1)
    ]),
    _createElementVNode("div", _hoisted_2, [
      _withDirectives(_createElementVNode("div", null, [
        _createVNode(_unref(Error), {
          errorLoc: _unref(ErrorLocationType).table
        }, null, 8, ["errorLoc"]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _withDirectives((_openBlock(), _createElementBlock("button", {
              class: "primary mr-2",
              onClick: newUser,
              title: _unref(t)('users.newUser')
            }, _hoisted_7, 8, _hoisted_5)), [
              [_directive_permission, 'UserAdministrator.User.Create']
            ]),
            _withDirectives((_openBlock(), _createElementBlock("button", {
              class: "primary mr-2",
              disabled: !_unref(selectedUsers).some(()=>true),
              onClick: exportCSV,
              title: _unref(t)('users.exportCsv')
            }, _hoisted_10, 8, _hoisted_8)), [
              [_directive_permission, 'UserAdministrator.User.CSV.Export']
            ]),
            _withDirectives((_openBlock(), _createElementBlock("button", {
              class: "primary mr-2",
              disabled: !_unref(selectedUsers).some(()=>true),
              onClick: deleteSelection,
              title: _unref(t)('global.delete')
            }, _hoisted_13, 8, _hoisted_11)), [
              [_directive_permission, 'UserAdministrator.User.Delete']
            ]),
            _withDirectives((_openBlock(), _createElementBlock("button", {
              class: "primary mr-2",
              disabled: !_unref(selectedUsers).some(()=>true),
              onClick: resetMultipleActivities,
              title: _unref(t)('global.reset')
            }, _hoisted_16, 8, _hoisted_14)), [
              [_directive_permission, 'UserAdministrator.User.Delete']
            ]),
            _createElementVNode("div", _hoisted_17, [
              _createVNode(_unref(GridInputFilter), {
                gridStore: _unref(gridStore),
                popupType: _unref(PopupType).userFilterPopup
              }, null, 8, ["gridStore", "popupType"])
            ])
          ]),
          _createVNode(_unref(BryntumGrid), _mergeProps({
            ref_key: "grid",
            ref: grid
          }, gridConfig, { readOnly: true }), null, 16)
        ])
      ], 512), [
        [_vShow, !_unref(globalStore).gridDataIsLoading]
      ]),
      _withDirectives(_createElementVNode("div", _hoisted_18, [
        _createVNode(LoadingSpinner)
      ], 512), [
        [_vShow, _unref(globalStore).gridDataIsLoading]
      ])
    ])
  ]))
}
}

})